import { WorldcupGrantSourceItemGameCode } from '../../../api/generated/worldcup/model/worldcupGrantSourceItemGameCode.ts';
import { WorldcupMatchItemResponseStatus } from '../../../api/generated/worldcup/model/worldcupMatchItemResponseStatus.ts';
import { WorldcupMatchItemResponseUiStatus } from '../../../api/generated/worldcup/model/worldcupMatchItemResponseUiStatus.ts';
import { WorldcupMyPredictionResponseResultStatus } from '../../../api/generated/worldcup/model/worldcupMyPredictionResponseResultStatus.ts';
import { WorldcupPredictionDtoResultStatus } from '../../../api/generated/worldcup/model/worldcupPredictionDtoResultStatus.ts';
import { WorldcupRoundDtoRoundCode } from '../../../api/generated/worldcup/model/worldcupRoundDtoRoundCode.ts';
import { WorldcupRoundDtoStatus } from '../../../api/generated/worldcup/model/worldcupRoundDtoStatus.ts';
import {
  createEmptyWorldcupPageViewModel,
  WORLD_CUP_BADGE_TONE,
  WORLD_CUP_CARD_STATUS,
  WORLD_CUP_GAME_LABEL,
  WORLD_CUP_PREDICTION_RESULT,
  WORLD_CUP_ROUND_LABEL,
  WORLD_CUP_ROUND_ORDER,
  WORLD_CUP_TAB_STATUS,
} from './worldcupViewModel';

const DEFAULT_POINTS_PER_TICKET = 50;
const WORLD_CUP_GRANT_SOURCE_ROUTE_BY_CODE = {
  [WorldcupGrantSourceItemGameCode.punch]: '/punch',
  [WorldcupGrantSourceItemGameCode.timeNyang]: '/time-nyang',
  [WorldcupGrantSourceItemGameCode.mulNyang]: '/mulnyang-bulnyang',
};

const formatPeriodLabel = (startAt, endAt) => {
  if (!startAt || !endAt) {
    return '';
  }

  const startDate = new Date(startAt);
  const endDate = new Date(endAt);

  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return '';
  }

  return `${startDate.getMonth() + 1}/${startDate.getDate()} ~ ${endDate.getMonth() + 1}/${endDate.getDate()}`;
};

const sortByRoundOrder = (left, right) => {
  return WORLD_CUP_ROUND_ORDER.indexOf(left.roundCode) - WORLD_CUP_ROUND_ORDER.indexOf(right.roundCode);
};

const sortRounds = (left, right) => {
  const leftSortOrder = left?.sortOrder;
  const rightSortOrder = right?.sortOrder;

  if (Number.isFinite(leftSortOrder) && Number.isFinite(rightSortOrder) && leftSortOrder !== rightSortOrder) {
    return leftSortOrder - rightSortOrder;
  }

  return sortByRoundOrder(left, right);
};

const normalizeRoundCode = (roundCode) => {
  if (WORLD_CUP_ROUND_ORDER.includes(roundCode)) {
    return roundCode;
  }

  return WorldcupRoundDtoRoundCode.R16;
};

const mapRoundStatusToTabStatus = (roundStatus) => {
  if (roundStatus === WorldcupRoundDtoStatus.OPEN) {
    return WORLD_CUP_TAB_STATUS.ACTIVE;
  }

  if (roundStatus === WorldcupRoundDtoStatus.CLOSED) {
    return WORLD_CUP_TAB_STATUS.COMPLETED;
  }

  return WORLD_CUP_TAB_STATUS.UPCOMING;
};

const mapUiStatusToCardStatus = (uiStatus, serverStatus) => {
  if (uiStatus === WorldcupMatchItemResponseUiStatus.BETTABLE) {
    return WORLD_CUP_CARD_STATUS.OPEN;
  }

  if (uiStatus === WorldcupMatchItemResponseUiStatus.CLOSED) {
    return WORLD_CUP_CARD_STATUS.CLOSED;
  }

  if (
    uiStatus === WorldcupMatchItemResponseUiStatus.WIN ||
    uiStatus === WorldcupMatchItemResponseUiStatus.LOSE ||
    uiStatus === WorldcupMatchItemResponseUiStatus.NOT_PARTICIPATED
  ) {
    return WORLD_CUP_CARD_STATUS.SETTLED;
  }

  if (uiStatus === WorldcupMatchItemResponseUiStatus.CANCELLED || serverStatus === WorldcupMatchItemResponseStatus.CANCELLED) {
    return WORLD_CUP_CARD_STATUS.CANCELLED;
  }

  return WORLD_CUP_CARD_STATUS.LOCKED;
};

const mapUiStatusToBadge = (uiStatus, cardStatus, myPrediction) => {
  if (uiStatus === WorldcupMatchItemResponseUiStatus.BETTABLE) {
    if (myPrediction?.pickedTeamId) {
      return { label: '예측진행중', tone: WORLD_CUP_BADGE_TONE.PROGRESS };
    }

    return { label: '예측가능', tone: WORLD_CUP_BADGE_TONE.OPEN };
  }

  if (uiStatus === WorldcupMatchItemResponseUiStatus.CLOSED) {
    return { label: '예측진행중', tone: WORLD_CUP_BADGE_TONE.PROGRESS };
  }

  if (uiStatus === WorldcupMatchItemResponseUiStatus.WIN) {
    return { label: '예측성공', tone: WORLD_CUP_BADGE_TONE.SUCCESS };
  }

  if (uiStatus === WorldcupMatchItemResponseUiStatus.LOSE) {
    return { label: '예측실패', tone: WORLD_CUP_BADGE_TONE.FAIL };
  }

  if (uiStatus === WorldcupMatchItemResponseUiStatus.NOT_PARTICIPATED) {
    return { label: '미참여', tone: WORLD_CUP_BADGE_TONE.UPCOMING };
  }

  if (uiStatus === WorldcupMatchItemResponseUiStatus.CANCELLED || cardStatus === WORLD_CUP_CARD_STATUS.CANCELLED) {
    return { label: '미참여', tone: WORLD_CUP_BADGE_TONE.UPCOMING };
  }

  return { label: '오픈예정', tone: WORLD_CUP_BADGE_TONE.UPCOMING };
};

const mapMyPredictionResult = (myPrediction, cardStatus) => {
  if (!myPrediction?.pickedTeamId) {
    return WORLD_CUP_PREDICTION_RESULT.NONE;
  }

  if (myPrediction.resultStatus === WorldcupMyPredictionResponseResultStatus.WIN) {
    return WORLD_CUP_PREDICTION_RESULT.WON;
  }

  if (myPrediction.resultStatus === WorldcupMyPredictionResponseResultStatus.LOSE) {
    return WORLD_CUP_PREDICTION_RESULT.LOST;
  }

  if (myPrediction.resultStatus === WorldcupMyPredictionResponseResultStatus.REFUND) {
    return WORLD_CUP_PREDICTION_RESULT.REFUNDED;
  }

  if (cardStatus === WORLD_CUP_CARD_STATUS.CLOSED || cardStatus === WORLD_CUP_CARD_STATUS.OPEN) {
    return WORLD_CUP_PREDICTION_RESULT.PICKED;
  }

  return WORLD_CUP_PREDICTION_RESULT.PENDING;
};

const mapTeamView = (team, pickedTeamId) => ({
  teamId: team?.teamId ?? null,
  name: team?.name ?? '미정',
  flagUrl: team?.flagUrl ?? null,
  pickRate: team?.pickRate ?? null,
  ticketSum: team?.ticketSum ?? 0,
  isMyPick: Boolean(team?.teamId && pickedTeamId && team.teamId === pickedTeamId),
});

const getCurrentRoundCode = (eventOverviewResponse, tabs, roundSections) => {
  const activeRoundCode = eventOverviewResponse?.activeRoundCode;

  if (WORLD_CUP_ROUND_ORDER.includes(activeRoundCode)) {
    return activeRoundCode;
  }

  const activeTab = tabs.find((tab) => tab.tabStatus === WORLD_CUP_TAB_STATUS.ACTIVE);

  if (activeTab) {
    return activeTab.roundCode;
  }

  const firstPlayableSection = roundSections.find((section) =>
    section.matches.some(
      (match) => match.cardStatus === WORLD_CUP_CARD_STATUS.OPEN || match.cardStatus === WORLD_CUP_CARD_STATUS.CLOSED,
    ),
  );

  if (firstPlayableSection) {
    return firstPlayableSection.roundCode;
  }

  const completedTab = [...tabs].reverse().find((tab) => tab.tabStatus === WORLD_CUP_TAB_STATUS.COMPLETED);

  return completedTab?.roundCode ?? tabs[0]?.roundCode ?? roundSections[0]?.roundCode ?? WorldcupRoundDtoRoundCode.R16;
};

export const mapEventOverviewToTabs = (eventOverviewResponse) => {
  const availableRounds = (eventOverviewResponse?.rounds ?? [])
    .filter((round) => WORLD_CUP_ROUND_ORDER.includes(round?.roundCode))
    .sort(sortRounds);

  const roundsToRender =
    availableRounds.length > 0
      ? availableRounds
      : WORLD_CUP_ROUND_ORDER.map((roundCode) => ({
          roundCode,
          roundName: WORLD_CUP_ROUND_LABEL[roundCode],
        }));

  return roundsToRender.map((round) => {
    const roundCode = round.roundCode;
    const tabStatus = mapRoundStatusToTabStatus(round?.status);

    return {
      roundId: round?.roundId ?? null,
      roundCode,
      label: round?.roundName ?? WORLD_CUP_ROUND_LABEL[roundCode],
      tabStatus,
      isActive: eventOverviewResponse?.activeRoundCode === roundCode || tabStatus === WORLD_CUP_TAB_STATUS.ACTIVE,
      openAt: round?.openAt ?? null,
      closedAt: round?.closedAt ?? null,
    };
  });
};

export const mapTicketStatusToSummary = (ticketStatusResponse) => {
  const grantSources = (ticketStatusResponse?.grantSources ?? []).map((source) => ({
    gameCode: source?.gameCode ?? WorldcupGrantSourceItemGameCode.punch,
    label: source?.label ?? WORLD_CUP_GAME_LABEL[source?.gameCode] ?? '',
    grantedToday: Boolean(source?.grantedToday),
    disabled: Boolean(source?.grantedToday),
    routePath: WORLD_CUP_GRANT_SOURCE_ROUTE_BY_CODE[source?.gameCode] ?? null,
  }));

  return {
    ticketBalance: ticketStatusResponse?.ticketBalance ?? 0,
    dailyLimit: ticketStatusResponse?.dailyLimit ?? 3,
    guideLines: [
      `각 게임당 티켓 1장, 매일 총 ${ticketStatusResponse?.dailyLimit ?? 3}개 획득 가능 (00:00 초기화)`,
      `참여 티켓 1장당 보상금 ${DEFAULT_POINTS_PER_TICKET}P 누적`,
      '적중 시 누적 포인트를 티켓 수만큼 분배',
      '경기당 티켓 최대 3장까지 참여 가능',
      '예측은 경기 시작 2시간 전 마감',
    ],
    grantSources,
  };
};

export const mapMatchItemToCardViewModel = (matchItem, roundCode) => {
  const cardStatus = mapUiStatusToCardStatus(matchItem?.uiStatus, matchItem?.status);
  const badge = mapUiStatusToBadge(matchItem?.uiStatus, cardStatus, matchItem?.myPrediction);
  const myPredictionResult = mapMyPredictionResult(matchItem?.myPrediction, cardStatus);
  const pickedTeamId = matchItem?.myPrediction?.pickedTeamId ?? null;
  const winnerTeamId =
    matchItem?.homeScore == null || matchItem?.awayScore == null
      ? null
      : matchItem.homeScore === matchItem.awayScore
        ? null
        : matchItem.homeScore > matchItem.awayScore
          ? matchItem?.homeTeam?.teamId ?? null
          : matchItem?.awayTeam?.teamId ?? null;

  return {
    matchId: matchItem?.matchId ?? null,
    roundCode: normalizeRoundCode(roundCode),
    kickoffAt: matchItem?.kickoffAt ?? null,
    bettingCloseAt: matchItem?.bettingCloseAt ?? null,
    serverStatus: matchItem?.status ?? null,
    uiStatus: matchItem?.uiStatus ?? null,
    cardStatus,
    badge,
    homeTeam: mapTeamView(matchItem?.homeTeam, pickedTeamId),
    awayTeam: mapTeamView(matchItem?.awayTeam, pickedTeamId),
    homeScore: matchItem?.homeScore ?? null,
    awayScore: matchItem?.awayScore ?? null,
    totalPoolPoint: matchItem?.totalPoolPoint ?? 0,
    estimatedPayoutPerTicket: matchItem?.estimatedPayoutPerTicket ?? null,
    pointsPerTicket: matchItem?.pointsPerTicket ?? DEFAULT_POINTS_PER_TICKET,
    totalTicketCount: (matchItem?.homeTeam?.ticketSum ?? 0) + (matchItem?.awayTeam?.ticketSum ?? 0),
    myPrediction: {
      pickedTeamId,
      ticketCount: matchItem?.myPrediction?.ticketCount ?? 0,
      result: myPredictionResult,
      earnedPoint: matchItem?.myPrediction?.earnedPoint ?? 0,
      hasPrediction: Boolean(pickedTeamId),
    },
    result: {
      winnerTeamId,
      pointPerTicket: matchItem?.estimatedPayoutPerTicket ?? 0,
    },
  };
};

export const mapMatchListToRoundSection = (matchListResponse) => ({
  roundCode: normalizeRoundCode(matchListResponse?.roundCode),
  roundStatus: matchListResponse?.roundStatus ?? null,
  matches: (matchListResponse?.matches ?? []).map((matchItem) =>
    mapMatchItemToCardViewModel(matchItem, matchListResponse?.roundCode),
  ),
});

export const mapPredictionHistoryToItems = (predictionHistoryResponse) => {
  return (predictionHistoryResponse?.predictions ?? []).map((prediction) => ({
    predictionId: prediction?.predictionId ?? null,
    eventId: prediction?.eventId ?? null,
    matchId: prediction?.matchId ?? null,
    pickedTeamId: prediction?.pickedTeamId ?? null,
    ticketCount: prediction?.ticketCount ?? 0,
    resultStatus: prediction?.resultStatus ?? WorldcupPredictionDtoResultStatus.PENDING,
    earnedPoint: prediction?.earnedPoint ?? 0,
    createdAt: prediction?.createdAt ?? null,
    settledAt: prediction?.settledAt ?? null,
  }));
};

export const buildWorldcupPageViewModel = ({
  eventOverviewResponse,
  ticketStatusResponse,
  matchListResponses = [],
  predictionHistoryResponse,
} = {}) => {
  const emptyPage = createEmptyWorldcupPageViewModel();
  const tabs = mapEventOverviewToTabs(eventOverviewResponse);
  const roundSections = matchListResponses.map(mapMatchListToRoundSection).sort(sortByRoundOrder);
  const currentRoundCode = getCurrentRoundCode(eventOverviewResponse, tabs, roundSections);

  return {
    ...emptyPage,
    event: {
      eventId: eventOverviewResponse?.event?.eventId ?? null,
      title: eventOverviewResponse?.event?.title ?? emptyPage.event.title,
      periodLabel: formatPeriodLabel(eventOverviewResponse?.event?.startAt, eventOverviewResponse?.event?.endAt),
      status: eventOverviewResponse?.event?.status ?? null,
    },
    currentRoundCode,
    activeRoundCode: eventOverviewResponse?.activeRoundCode ?? null,
    tabs: tabs.map((tab) => ({
      ...tab,
      isActive: tab.roundCode === currentRoundCode,
    })),
    ticketSummary: mapTicketStatusToSummary(ticketStatusResponse),
    roundSections,
    predictionHistory: mapPredictionHistoryToItems(predictionHistoryResponse),
  };
};
